<template>
  <footer v-if="enable_footer" class="px-4 pb-4 bg-white sm:px-6">
    <hr class="my-6 border-gray-300 sm:mx-auto" />
    <!-- 
        Added md:items-start and md:space-x-8 to create spacing 
        between the left and right columns on larger screens 
      -->
    <div class="md:flex md:justify-between md:items-start md:space-x-8">
      <!-- Left Column: Logo, Description, Social Icons -->
      <!-- Left Column: Logo, Description, Social Icons -->
      <div class="mt-4 md:mb-0">
        <a
          href="https://www.zakonyprolidi.cz"
          target="_blank"
          class="flex items-center"
        >
          <img
            v-if="eventType.images.logo"
            :src="eventType.images.logo.big"
            alt="Logo"
            class="mr-4 h-14"
          />
          <span v-else class="text-xl font-semibold whitespace-nowrap">
            {{ eventType.name }}
          </span>
        </a>
        <p class="text-gray-500 mt-3 max-w-sm">
          Všechny předpisy Sbírky zákonů v aktuálním znění. Pro Vás k dispozici
          ve třech způsobech používání.
        </p>

        <!-- Social Icons -->
        <div class="mt-4 flex items-center space-x-4 py-4">
          <a
            href="https://www.facebook.com/zakonyprolidi"
            target="_blank"
            title="Přidejte si Zákony pro lidi na Facebooku"
            class="text-gray-500 hover:text-gray-700"
          >
            <font-awesome-icon :icon="['fab', 'facebook']" class="w-6 h-6" />
          </a>
          <a
            href="https://twitter.com/zakonyprolidi"
            target="_blank"
            title="Přidejte si Zákony pro lidi na Twitteru"
            class="text-gray-500 hover:text-gray-700"
          >
            <font-awesome-icon :icon="['fab', 'twitter']" class="w-6 h-6" />
          </a>
          <a
            href="https://instagram.com/zakony_pro_lidi"
            target="_blank"
            title="Přidejte si Zákony pro lidi na Instagramu"
            class="text-gray-500 hover:text-gray-700"
          >
            <font-awesome-icon :icon="['fab', 'instagram']" class="w-6 h-6" />
          </a>
          <a
            href="https://www.linkedin.com/company/zakony-pro-lidi/"
            target="_blank"
            title="Přidejte si Zákony pro lidi na LinkedInu"
            class="text-gray-500 hover:text-gray-700"
          >
            <font-awesome-icon :icon="['fab', 'linkedin']" class="w-6 h-6" />
          </a>
          <a
            href="https://www.zakonyprolidi.cz/feedback"
            title="Napište nám Vaše nápady a připomínky"
            class="text-gray-500 hover:text-gray-700"
          >
            <font-awesome-icon :icon="['fas', 'envelope']" class="w-6 h-6" />
          </a>
          <a
            href="/rss.xml"
            title="RSS Feed"
            class="text-orange-500 hover:text-gray-700"
          >
            <font-awesome-icon :icon="['fas', 'rss']" class="w-6 h-6" />
          </a>
        </div>

        <!-- JSON Feed Link for Developers -->
        <div class="mt-4">
          <a
            href="/feed_v1.json"
            target="_blank"
            class="text-sm text-gray-500 hover:underline"
          >
            JSON Feed pro vývojáře
          </a>
        </div>
      </div>

      <!-- Right Column: Account & Legal Info -->
      <!-- 
          On mobile, this appears below the left column. 
          Added mt-8 so there's vertical spacing on small screens. 
          On md and above, they sit side by side with space-x-8. 
        -->
      <div class="grid grid-cols-2 gap-8 sm:gap-6 mt-8 md:mt-0">
        <!-- Show account links when the user is not logged in -->
        <div v-if="!isLoggedIn">
          <h3 class="mb-3 text-sm font-semibold text-gray-900 uppercase">
            {{ $translate("User Account") }}
          </h3>
          <ul>
            <li class="mb-4">
              <router-link
                :to="{ path: `/${$systemPath('login')}` }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Login") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{ path: `/${$systemPath('register')}` }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Register") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{ path: `/${$systemPath('forgotPassword')}` }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Password Recovery") }}
              </router-link>
            </li>
          </ul>
        </div>

        <div v-else>
          <h3 class="mb-3 text-sm font-semibold text-gray-900 uppercase">
            {{ $translate("Account Settings") }}
          </h3>
          <ul>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'UserAccount',
                  params: { id: $systemPath('settings') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Settings") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'UserAccount',
                  params: { id: $systemPath('payments') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Payments & Invoices") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'UserAccount',
                  params: { id: $systemPath('billingAddress') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Billing Address") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'UserAccount',
                  params: { id: $systemPath('preferences') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Preferences") }}
              </router-link>
            </li>
          </ul>
        </div>

        <div v-if="enable_legalpages">
          <h3 class="mb-3 text-sm font-semibold text-gray-900 uppercase">
            {{ $translate("Legal information") }}
          </h3>
          <ul>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'LegalPages',
                  params: { id: $systemPath('terms') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Terms & Conditions") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'LegalPages',
                  params: { id: $systemPath('privacy') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Privacy Policy") }}
              </router-link>
            </li>
            <li class="mb-4">
              <router-link
                :to="{
                  name: 'LegalPages',
                  params: { id: $systemPath('cookiePolicy') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("Cookies Policy") }}
              </router-link>
            </li>
            <li>
              <router-link
                :to="{
                  name: 'LegalPages',
                  params: { id: $systemPath('contact') },
                }"
                class="text-gray-600 hover:underline"
              >
                {{ $translate("contact_us") }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <hr class="my-4 border-gray-300 sm:mx-auto" />
    <!-- 
        Footer Bottom Row 
        1) &copy; text is always visible.
        2) "Máte zájem..." text is hidden on mobile: hidden below md breakpoint. 
      -->
    <div class="sm:flex sm:items-center sm:justify-between">
      <span class="text-sm text-gray-500 sm:text-center">
        &copy; {{ new Date().getFullYear() }} {{ $TWORK_INFO.name }}.
        {{ $translate("All Rights Reserved") }}.
      </span>
      <!-- Hide on mobile, show on md+ -->
      <div
        class="hidden md:flex items-center justify-center space-x-2 text-sm text-gray-500"
      >
        <font-awesome-icon
          :icon="['fas', 'code']"
          class="w-4 h-4 text-blue-500"
        />
        <span> Máte zájem o podobný systém? </span>
        <a href="mailto:sarkak@aion.cz" class="text-blue-600 hover:underline">
          Napište nám.
        </a>
      </div>
    </div>
  </footer>
</template>
  
  <script>
import { getUserData } from "@/utils/cookie";
import { ref } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";

// Add brand and solid icons to the library
library.add(
  faFacebook,
  faTwitter,
  faInstagram,
  faLinkedin,
  faYoutube,
  faEnvelope
);

export default {
  name: "FooterModule",
  components: {
    FontAwesomeIcon,
  },
  props: {
    isSidebarOpen: {
      type: Boolean,
      default: false,
      required: false,
    },
    eventType: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoggedIn: false,
    };
  },
  setup() {
    const enable_legalpages = ref(
      window.vueAppConfig?.settings?.enable_legalpages || false
    );
    const enable_footer = ref(
      window.vueAppConfig?.settings?.enable_footer || false
    );
    return {
      enable_legalpages,
      enable_footer,
    };
  },
  mounted() {
    const user = getUserData();
    if (user && user.type === "partner") {
      this.isLoggedIn = true;
    }
  },
};
</script>
  
  <style scoped>
.event-content {
  padding: 2rem 3rem 2rem 1.5rem;
  overflow-y: auto;
}

@media (max-width: 639px) {
  .url-socials {
    margin-top: 1rem;
  }
}
</style>
  